import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { MapContainer, TileLayer, Polyline, CircleMarker, Marker, Tooltip, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { Radio } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const POLL_MS = 30000;
const LIVE_WINDOW_MS = 2 * 60 * 1000;
export const DRIVER_HEX = { Lippe: '#f43f5e', Sebastiano: '#3b82f6', Daniel: '#10b981', Sandro: '#f59e0b' };

const fmtTime = (iso) => (iso ? new Date(iso).toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit' }) : '');

const driverStatus = (d) => {
  if (d.last_seen && d.tracking && Date.now() - new Date(d.last_seen).getTime() < LIVE_WINDOW_MS) return { live: true, text: 'Live' };
  if (d.last_seen) return { live: false, text: `Laatst gezien ${new Date(d.last_seen).toLocaleString('nl-NL', { weekday: 'short', hour: '2-digit', minute: '2-digit' })}` };
  return { live: false, text: 'Rit niet gestart' };
};

const stopIcon = (n, color, done) => L.divIcon({
  className: '',
  html: `<div style="width:24px;height:24px;border-radius:9999px;background:${done ? '#059669' : color};color:#fff;font:700 12px/24px sans-serif;text-align:center;border:2px solid #fff;box-shadow:0 1px 4px rgba(0,0,0,.4)">${n}</div>`,
  iconSize: [24, 24],
  iconAnchor: [12, 12],
});

const driverIcon = (name, color, live) => L.divIcon({
  className: '',
  html: `<div style="display:flex;align-items:center;gap:4px;transform:translate(-14px,-14px)"><div style="width:28px;height:28px;border-radius:9999px;background:${color};border:3px solid #fff;box-shadow:0 0 0 ${live ? 6 : 0}px ${color}55,0 2px 6px rgba(0,0,0,.4)"></div><span style="background:#18181b;color:#fff;font:600 11px sans-serif;padding:2px 6px;border-radius:6px;white-space:nowrap">${name}</span></div>`,
  iconSize: [0, 0],
});

const FitOnce = ({ points }) => {
  const map = useMap();
  const done = useRef(false);
  useEffect(() => {
    if (done.current || points.length === 0) return;
    map.fitBounds(L.latLngBounds(points), { padding: [40, 40], maxZoom: 12, animate: false });
    done.current = true;
  }, [points, map]);
  return null;
};

const DriverLayer = ({ d }) => {
  const color = DRIVER_HEX[d.name] || '#7c3aed';
  const status = driverStatus(d);
  return (
    <>
      {d.trail.length > 1 && <Polyline positions={d.trail} pathOptions={{ color, weight: 4, opacity: 0.7 }} />}
      {d.stops.map((s, i) => s.position && (
        <Marker key={`${d.name}-stop-${i}`} position={s.position} icon={stopIcon(i + 1, color, s.motors.every((m) => m.picked_up))}>
          <Popup>
            <b>{i + 1}. {s.company}</b> <span style={{ color }}>({d.name})</span><br />
            {s.address}<br />
            {s.motors.map((m) => (
              <div key={m.order_id}>{m.picked_up ? '✅' : '⬜'} {m.label}{m.picked_up_at ? ` — ${fmtTime(m.picked_up_at)}` : ''}</div>
            ))}
          </Popup>
        </Marker>
      ))}
      {d.stops.flatMap((s) => s.motors).filter((m) => m.picked_up_location).map((m) => (
        <CircleMarker key={`p-${m.order_id}`} center={[m.picked_up_location.lat, m.picked_up_location.lon]} radius={6}
                      pathOptions={{ color: '#fff', weight: 2, fillColor: '#059669', fillOpacity: 1 }}>
          <Tooltip>{m.label} opgehaald {fmtTime(m.picked_up_at)} door {d.name}</Tooltip>
        </CircleMarker>
      ))}
      {d.last_location && (
        <Marker position={[d.last_location.lat, d.last_location.lon]} icon={driverIcon(d.name, color, status.live)} zIndexOffset={1000}>
          <Popup><b>{d.name}</b><br />{status.text}</Popup>
        </Marker>
      )}
    </>
  );
};

const DriverLegend = ({ drivers }) => (
  <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mt-3">
    {drivers.map((d) => {
      const status = driverStatus(d);
      const picked = d.stops.flatMap((s) => s.motors).filter((m) => m.picked_up);
      const total = d.stops.reduce((n, s) => n + s.motors.length, 0);
      return (
        <div key={d.name} className="border border-zinc-200 rounded-lg p-2.5 text-xs" data-testid={`live-driver-${d.name}`}>
          <div className="flex items-center gap-2">
            <span className="w-3 h-3 rounded-full" style={{ background: DRIVER_HEX[d.name] }} />
            <span className="font-semibold text-zinc-900 text-sm">{d.name}</span>
            <span className={`ml-auto font-medium ${status.live ? 'text-emerald-600' : 'text-zinc-500'}`} data-testid={`live-status-${d.name}`}>
              {status.live && '● '}{status.text}
            </span>
          </div>
          <div className="text-zinc-600 mt-1">{picked.length}/{total} opgehaald</div>
          {picked.map((m) => (
            <div key={m.order_id} className="text-zinc-500">✅ {m.label} · {fmtTime(m.picked_up_at)}{m.picked_up_location ? ' · 📍' : ''}</div>
          ))}
        </div>
      );
    })}
  </div>
);

export const LiveMap = ({ token }) => {
  const [data, setData] = useState(null);

  useEffect(() => {
    let alive = true;
    const load = () => axios.get(`${API}/admin/chauffeur-live`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => alive && setData(r.data)).catch(() => {});
    load();
    const t = setInterval(load, POLL_MS);
    return () => { alive = false; clearInterval(t); };
  }, [token]);

  const points = useMemo(() => {
    if (!data) return [];
    return data.drivers.flatMap((d) => [
      ...d.trail,
      ...d.stops.map((s) => s.position).filter(Boolean),
      ...(d.last_location ? [[d.last_location.lat, d.last_location.lon]] : []),
    ]);
  }, [data]);

  if (!data || data.drivers.length === 0) return null;
  return (
    <div className="bg-white rounded-xl border border-zinc-200 p-4" data-testid="live-map-card">
      <h2 className="text-sm font-semibold text-zinc-900 flex items-center gap-2 mb-3">
        <Radio className="w-4 h-4 text-emerald-600" /> Live volgen
        <span className="text-xs font-normal text-zinc-500">· ververst elke 30 sec · spoor van vandaag</span>
      </h2>
      <div className="h-[420px] rounded-lg overflow-hidden border border-zinc-200 relative z-0" data-testid="live-map">
        <MapContainer center={data.hq} zoom={6} style={{ height: '100%', width: '100%' }} scrollWheelZoom zoomAnimation={false} markerZoomAnimation={false}>
          <TileLayer attribution='&copy; OpenStreetMap' url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
          <FitOnce points={points.length ? [...points, data.hq, data.end] : []} />
          <CircleMarker center={data.hq} radius={7} pathOptions={{ color: '#18181b', fillColor: '#18181b', fillOpacity: 1 }}>
            <Tooltip>Start: Schalkhaar</Tooltip>
          </CircleMarker>
          <CircleMarker center={data.end} radius={7} pathOptions={{ color: '#18181b', fillColor: '#fff', fillOpacity: 1, weight: 3 }}>
            <Tooltip>Eind: Coppet</Tooltip>
          </CircleMarker>
          {data.drivers.map((d) => <DriverLayer key={d.name} d={d} />)}
        </MapContainer>
      </div>
      <DriverLegend drivers={data.drivers} />
    </div>
  );
};
